import React, { useState, useEffect, useCallback } from 'react';
import './AdminDashboard.css';
import './ChatSessionsPage.css';
import logo from '../assets/logo.png';
import PropertyManagersPage from './PropertyManagersPage';
import PropertiesPage from './PropertiesPage';
import GuidebooksPage from './GuidebooksPage';
import ChatSessionsPage, { ChatModal } from './ChatSessionsPage';

const API_BASE = process.env.REACT_APP_API_BASE;

function getToken() {
  return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
}

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmt(n) {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

const PAGE_TITLES = {
  dashboard:     'Dashboard',
  managers:      'Property Managers',
  properties:    'Properties',
  guidebooks:    'Guidebooks',
  chat_sessions: 'Chat Sessions',
};


const NAV_ITEMS = [
  { id: 'dashboard',         label: 'Dashboard',          icon: DashIcon   },
  { id: 'managers',          label: 'Property Managers',  icon: PeopleIcon, adminOnly: true },
  { id: 'properties',        label: 'Properties',         icon: HomeIcon   },
  { id: 'guidebooks',        label: 'Guidebooks',         icon: BookIcon   },
  { id: 'chat_sessions',     label: 'Chat Sessions',      icon: ChatIcon   },
];

const ADMIN_KPI = [
  { key: 'guidebooks', label: 'Guidebooks',      sub: 'registered',        Icon: BookIcon,   accent: '#C8102E', bg: 'rgba(200,16,46,0.09)', navTo: 'guidebooks'    },
  { key: 'managers',   label: 'Active Managers', sub: 'property managers', Icon: PeopleIcon, accent: '#1A2744', bg: 'rgba(26,39,68,0.09)',  navTo: 'managers'      },
  { key: 'sessions',   label: 'Chat Sessions',   sub: 'total',             Icon: ChatIcon,   accent: '#2a5298', bg: 'rgba(42,82,152,0.09)', navTo: 'chat_sessions' },
  { key: 'messages',   label: 'Messages',        sub: 'exchanged',         Icon: MsgIcon,    accent: '#7c2d12', bg: 'rgba(124,45,18,0.09)', navTo: 'chat_sessions' },
];

const PM_KPI = [
  { key: 'properties', label: 'My Properties', sub: 'assigned to you',  Icon: HomeIcon,   accent: '#2a5298', bg: 'rgba(42,82,152,0.09)', navTo: 'properties'    },
  { key: 'guidebooks', label: 'Guidebooks',    sub: 'linked to yours',   Icon: BookIcon,   accent: '#C8102E', bg: 'rgba(200,16,46,0.09)', navTo: 'guidebooks'    },
  { key: 'sessions',   label: 'Chat Sessions', sub: 'total',             Icon: ChatIcon,   accent: '#1A2744', bg: 'rgba(26,39,68,0.09)',  navTo: 'chat_sessions' },
  { key: 'messages',   label: 'Messages',      sub: 'exchanged',         Icon: MsgIcon,    accent: '#7c2d12', bg: 'rgba(124,45,18,0.09)', navTo: 'chat_sessions' },
];

// ── SVG icons ─────────────────────────────────────────────────────────────────

function DashIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
      <rect x="2" y="2" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="11" y="2" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="2" y="11" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="11" y="11" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
      <circle cx="7" cy="6" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M1 17c0-3 2.686-5 6-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="14" cy="6" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M10 17c0-3 2.686-5 6-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function HomeIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
      <path d="M2 9L10 2l8 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M4 8v9a1 1 0 001 1h10a1 1 0 001-1V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="7.5" y="12" width="5" height="6" rx="0.5" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  );
}

function BookIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
      <path d="M4 3h10a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M7 7h6M7 10h6M7 13h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
      <path d="M3 4h14a1 1 0 011 1v8a1 1 0 01-1 1H6l-4 3V5a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  );
}

function MsgIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M7 10h6M7 7h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
      <path d="M13 10H3m10 0l-3-3m3 3l-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8 6V5a2 2 0 012-2h5a2 2 0 012 2v10a2 2 0 01-2 2h-5a2 2 0 01-2-2v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="15" height="15">
      <path d="M4 4a7 7 0 0112 5M16 16a7 7 0 01-12-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <path d="M16 4v5h-5M4 16v-5h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ── Skeleton loader ────────────────────────────────────────────────────────────

function Skeleton({ w = '100%', h = 20, r = 6 }) {
  return (
    <div
      className="ad-skeleton"
      style={{ width: w, height: h, borderRadius: r }}
    />
  );
}

// ── SVG Chart components ───────────────────────────────────────────────────────

function TrendChart({ data }) {
  if (!data || data.length === 0) {
    return <div className="ad-chart-empty">No session data for this period.</div>;
  }

  const W = 540, H = 175;
  const pl = 34, pr = 14, pt = 22, pb = 32;
  const cw = W - pl - pr, ch = H - pt - pb;
  const n  = data.length;

  const maxSess = Math.max(...data.map(d => d.sessions), 1);
  const maxMsg  = Math.max(...data.map(d => d.messages), 1);

  const sessPts = data.map((d, i) => ({
    x: pl + (i / Math.max(n - 1, 1)) * cw,
    y: pt + ch - (d.sessions / maxSess) * ch,
    d,
  }));
  const msgPts = data.map((d, i) => ({
    x: pl + (i / Math.max(n - 1, 1)) * cw,
    y: pt + ch - (d.messages / maxMsg) * ch,
  }));

  const toPath = pts =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  const sessLine = toPath(sessPts);
  const sessArea = `${sessLine} L${sessPts[n-1].x.toFixed(1)},${(pt+ch).toFixed(1)} L${sessPts[0].x.toFixed(1)},${(pt+ch).toFixed(1)}Z`;
  const msgLine  = toPath(msgPts);

  const yTicks = [0, Math.ceil(maxSess / 2), maxSess];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
      <defs>
        <linearGradient id="adSessGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#C8102E" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#C8102E" stopOpacity="0.01" />
        </linearGradient>
      </defs>

      {/* Grid */}
      {yTicks.map(v => {
        const y = pt + ch - (v / maxSess) * ch;
        return (
          <g key={v}>
            <line x1={pl} y1={y} x2={pl + cw} y2={y} stroke="#f0f4fb" strokeWidth="1" />
            <text x={pl - 5} y={y} textAnchor="end" dominantBaseline="middle" fontSize="9" fill="#b0bdd4">{v}</text>
          </g>
        );
      })}

      {/* Area fill */}
      <path d={sessArea} fill="url(#adSessGrad)" />

      {/* Messages line (secondary, dashed navy) */}
      <path d={msgLine} fill="none" stroke="#2a5298" strokeWidth="1.5"
            strokeDasharray="4,2" strokeLinejoin="round" strokeLinecap="round" opacity="0.45" />

      {/* Sessions line */}
      <path d={sessLine} fill="none" stroke="#C8102E" strokeWidth="2"
            strokeLinejoin="round" strokeLinecap="round" />

      {/* Dots + native tooltip */}
      {sessPts.map((p, i) => (
        <g key={i} style={{ cursor: 'default' }}>
          <title>{p.d.date} — {p.d.sessions} sessions · {p.d.messages} messages</title>
          <circle cx={p.x} cy={p.y} r="3.5" fill="#C8102E" />
          <circle cx={p.x} cy={p.y} r="10" fill="transparent" />
        </g>
      ))}

      {/* X labels (every other) */}
      {sessPts.map((p, i) => i % 2 === 0 && (
        <text key={i} x={p.x} y={H - 4} textAnchor="middle" fontSize="9" fill="#b0bdd4">{p.d.date}</text>
      ))}

      {/* Axes */}
      <line x1={pl} y1={pt} x2={pl} y2={pt + ch} stroke="#e8eef8" strokeWidth="1" />
      <line x1={pl} y1={pt + ch} x2={pl + cw} y2={pt + ch} stroke="#e8eef8" strokeWidth="1" />

      {/* Legend */}
      <circle cx={pl} cy={pt - 8} r="3" fill="#C8102E" />
      <text x={pl + 7} y={pt - 4} fontSize="9" fill="#8a9bb8">Sessions</text>
      <line x1={pl + 58} y1={pt - 8} x2={pl + 70} y2={pt - 8} stroke="#2a5298" strokeWidth="1.5" strokeDasharray="3,2" opacity="0.6" />
      <text x={pl + 74} y={pt - 4} fontSize="9" fill="#8a9bb8">Messages</text>
    </svg>
  );
}

function GuidebooksChart({ data }) {
  if (!data || data.length === 0) {
    return <div className="ad-chart-empty">No sessions recorded yet.</div>;
  }

  const maxS  = Math.max(...data.map(d => d.sessions), 1);
  const ROW   = 36, LABW = 138, BARW = 182, PAD = 6;
  const W     = LABW + BARW + 36;
  const H     = data.length * ROW + PAD * 2;
  const COLS  = ['#C8102E', '#1A2744', '#2a5298', '#16a34a', '#d97706', '#7c3aed'];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
      {data.map((d, i) => {
        const bw    = Math.max((d.sessions / maxS) * BARW, 3);
        const y     = PAD + i * ROW;
        const col   = COLS[i % COLS.length];
        const lbl   = d.title.length > 17 ? d.title.slice(0, 16) + '…' : d.title;
        const inner = bw >= 32;

        return (
          <g key={i} style={{ cursor: 'default' }}>
            <title>{d.title}: {d.sessions} sessions · {d.messages} messages</title>
            <text x={LABW - 8} y={y + ROW / 2} textAnchor="end"
                  dominantBaseline="middle" fontSize="11" fill="#4a5a78">{lbl}</text>
            <rect x={LABW} y={y + 8} width={BARW} height={20} rx="4" fill="#f0f4fb" />
            <rect x={LABW} y={y + 8} width={bw}   height={20} rx="4" fill={col} opacity="0.82" />
            <text
              x={inner ? LABW + bw / 2 : LABW + bw + 5}
              y={y + ROW / 2}
              textAnchor={inner ? 'middle' : 'start'}
              dominantBaseline="middle"
              fontSize="10"
              fontWeight="600"
              fill={inner ? '#fff' : '#4a5a78'}
            >{d.sessions}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function AdminDashboard({ user, onLogout }) {
  const isPM = user.user_type === 'property_manager';
  const kpiConfig = isPM ? PM_KPI : ADMIN_KPI;

  const [activeNav, setActiveNav]         = useState('dashboard');
  const [stats, setStats]                 = useState(null);
  const [sessions, setSessions]           = useState([]);
  const [chartData, setChartData]         = useState(null);
  const [loadingStats, setLoadingStats]   = useState(true);
  const [loadingSess, setLoadingSess]     = useState(true);
  const [loadingChart, setLoadingChart]   = useState(true);
  const [statsErr, setStatsErr]           = useState('');
  const [sidebarOpen, setSidebarOpen]     = useState(false);
  const [chatSession, setChatSession]     = useState(null);

  const initials = (user.name || user.username || 'A')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const fetchDashboard = useCallback(async () => {
    const token = getToken();
    const headers = { Authorization: `Bearer ${token}` };

    setLoadingStats(true);
    setLoadingSess(true);
    setLoadingChart(true);
    setStatsErr('');

    try {
      const res = await fetch(`${API_BASE}/api/dashboard/stats`, { headers });
      if (!res.ok) throw new Error(await res.text());
      setStats(await res.json());
    } catch {
      setStatsErr('Could not load statistics.');
    } finally {
      setLoadingStats(false);
    }

    try {
      const res = await fetch(`${API_BASE}/api/dashboard/recent-sessions?limit=10`, { headers });
      if (res.ok) setSessions(await res.json());
    } catch {
      // silent — table just stays empty
    } finally {
      setLoadingSess(false);
    }

    try {
      const res = await fetch(`${API_BASE}/api/dashboard/chart-data`, { headers });
      if (res.ok) setChartData(await res.json());
    } catch {
      // silent — charts stay empty
    } finally {
      setLoadingChart(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  useEffect(() => { subscribeManagerToPush(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
  }

  async function subscribeManagerToPush() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('[Push] serviceWorker or PushManager not available');
      return;
    }
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      console.log('[Push] SW registered', reg);
      await navigator.serviceWorker.ready;
      console.log('[Push] SW ready');
      const permission = await Notification.requestPermission();
      console.log('[Push] permission:', permission);
      if (permission !== 'granted') return;
      const keyRes = await fetch(`${API_BASE}/api/chat/vapid-public-key`);
      console.log('[Push] VAPID key response status:', keyRes.status);
      if (!keyRes.ok) { console.warn('[Push] VAPID key fetch failed — push not configured'); return; }
      const { public_key } = await keyRes.json();
      console.log('[Push] VAPID public key received (first 20):', public_key?.slice(0, 20));
      const existing = await reg.pushManager.getSubscription();
      console.log('[Push] existing subscription:', existing?.endpoint?.slice(0, 40));
      const subscription = existing || await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key),
      });
      console.log('[Push] subscription created/found');
      const sub = subscription.toJSON();
      const saveRes = await fetch(`${API_BASE}/api/auth/manager-push-subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ endpoint: sub.endpoint, p256dh: sub.keys.p256dh, auth: sub.keys.auth }),
      });
      console.log('[Push] save subscription response:', saveRes.status);
    } catch (err) {
      console.error('[Push] subscribeManagerToPush failed:', err);
    }
  }

  return (
    <div className="ad-wrapper">

      {/* ── Mobile overlay ── */}
      {sidebarOpen && (
        <div className="ad-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ── */}
      <aside className={`ad-sidebar ${sidebarOpen ? 'ad-sidebar--open' : ''}`}>
        <div className="ad-logo">
          <img src={logo} alt="Logo" className="ad-logo-img" />
        </div>

        <nav className="ad-nav">
          {NAV_ITEMS.filter(n => !n.adminOnly || user.user_type === 'admin').map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={`ad-nav-item ${activeNav === id ? 'active' : ''}`}
              onClick={() => { setActiveNav(id); setSidebarOpen(false); }}
            >
              <span className="ad-nav-icon"><Icon /></span>
              {label}
            </button>
          ))}
        </nav>

        <div className="ad-sidebar-footer">
          <div className="ad-user-chip">
            <div className="ad-user-initials">{initials}</div>
            <div className="ad-user-info">
              <span className="ad-user-name">{user.name || user.username}</span>
              <span className="ad-user-role">
                {user.user_type === 'admin' ? 'Administrator' : 'Property Manager'}
              </span>
            </div>
          </div>
          <button className="ad-logout-btn" onClick={onLogout}>
            <LogoutIcon /> Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="ad-main">

        {/* Top bar */}
        <header className="ad-topbar">
          <button
            className="ad-hamburger"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
          <div className="ad-topbar-title">
            <h1>{PAGE_TITLES[activeNav] || 'Dashboard'}</h1>
            <p className="ad-topbar-date">{today}</p>
          </div>
          {activeNav === 'dashboard' && (
            <button
              className="ad-refresh-btn"
              onClick={fetchDashboard}
              title="Refresh data"
            >
              <RefreshIcon /> Refresh
            </button>
          )}
        </header>

        {/* Content */}
        <div className="ad-content">

          {/* ── Property Managers page ── */}
          {activeNav === 'managers' && user.user_type === 'admin' && (
            <PropertyManagersPage />
          )}

          {activeNav === 'properties' && <PropertiesPage user={user} />}

          {activeNav === 'guidebooks' && <GuidebooksPage user={user} />}

          {activeNav === 'chat_sessions' && <ChatSessionsPage user={user} />}

          {/* ── Dashboard content ── */}
          {activeNav === 'dashboard' && <>

          {statsErr && (
            <div className="ad-alert">{statsErr}</div>
          )}

          {/* ── KPI grid ── */}
          <div className="ad-kpi-grid">
            {kpiConfig.map(({ key, label, sub, Icon, accent, bg, navTo }) => (
              <div key={key} className="ad-kpi-card" style={{ '--accent': accent, cursor: navTo ? 'pointer' : 'default' }} onClick={() => navTo && setActiveNav(navTo)}>
                <div className="ad-kpi-top">
                  <div>
                    {loadingStats
                      ? <Skeleton w={70} h={36} r={8} />
                      : <span className="ad-kpi-num">{fmt(stats?.[key] ?? 0)}</span>
                    }
                    <span className="ad-kpi-label">{label}</span>
                  </div>
                  <div className="ad-kpi-icon" style={{ background: bg, color: accent }}>
                    <Icon />
                  </div>
                </div>
                <div className="ad-kpi-sub">{sub}</div>
              </div>
            ))}
          </div>

          {/* ── Analysis Charts ── */}
          <div className="ad-chart-grid">

            {/* Sessions Trend */}
            <section className="ad-panel">
              <div className="ad-panel-head">
                <h3>Sessions Trend <span className="ad-chart-period">— last 14 days</span></h3>
              </div>
              <div className="ad-chart-body">
                {loadingChart ? (
                  <div className="ad-chart-skeleton">
                    <Skeleton w="100%" h={140} r={8} />
                  </div>
                ) : (
                  <TrendChart data={chartData?.sessions_by_day} />
                )}
              </div>
            </section>

            {/* Top Guidebooks */}
            <section className="ad-panel">
              <div className="ad-panel-head">
                <h3>Top Guidebooks</h3>
                <span className="ad-panel-badge">by sessions</span>
              </div>
              <div className="ad-chart-body">
                {loadingChart ? (
                  <div className="ad-chart-skeleton">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} w="100%" h={24} r={6} />)}
                  </div>
                ) : (
                  <GuidebooksChart data={chartData?.by_guidebook} />
                )}
              </div>
            </section>

          </div>

          {/* ── Lower grid ── */}
          <div className="ad-lower-grid">

            {/* Recent sessions */}
            <section className="ad-panel">
              <div className="ad-panel-head">
                <h3>{isPM ? 'My Recent Chat Sessions' : 'Recent Chat Sessions'}</h3>
                <span className="ad-panel-badge">{sessions.length} shown</span>
              </div>
              <div className="ad-table-wrap">
                {loadingSess ? (
                  <div className="ad-skeleton-rows">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} h={42} r={8} />)}
                  </div>
                ) : sessions.length === 0 ? (
                  <div className="ad-empty">No sessions yet.</div>
                ) : (
                  <table className="ad-table">
                    <thead>
                      <tr>
                        <th>Guidebook</th>
                        <th>Session ID</th>
                        <th>Messages</th>
                        <th>Last Msg</th>
                        <th>Started</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.map((s) => (
                        <tr key={s.session_id}>
                          <td className="ad-td-book">{s.guidebook_title || s.guideid || '—'}</td>
                          <td>
                            <button
                              className="ad-session-link"
                              onClick={() => setChatSession(s.session_id)}
                              title="View chat history"
                            >
                              <code className="ad-code">{s.session_id.slice(0, 8)}…</code>
                            </button>
                          </td>
                          <td>{s.total_messages}</td>
                          <td className="ad-td-time">{timeAgo(s.last_message_at)}</td>
                          <td className="ad-td-time">{timeAgo(s.session_start)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            {/* Overview panel */}
            <section className="ad-panel ad-overview">
              <div className="ad-panel-head">
                <h3>{isPM ? 'My Session Overview' : 'Session Overview'}</h3>
              </div>

              <div className="ad-ov-hero">
                <div className="ad-ov-circle">
                  <span className="ad-ov-big">
                    {loadingStats ? '—' : fmt(stats?.active_sessions ?? 0)}
                  </span>
                  <span className="ad-ov-tiny">active now</span>
                </div>
              </div>

              <div className="ad-ov-rows">
                {[
                  { label: 'Started today',                    value: stats?.today_sessions },
                  { label: isPM ? 'My sessions' : 'Total sessions',  value: stats?.sessions },
                  { label: isPM ? 'My messages' : 'Total messages',  value: stats?.messages },
                  { label: 'Total tokens',                     value: stats?.total_tokens   },
                ].map(({ label, value }) => (
                  <div key={label} className="ad-ov-row">
                    <span className="ad-ov-label">{label}</span>
                    <span className="ad-ov-value">
                      {loadingStats ? <Skeleton w={40} h={14} /> : fmt(value ?? 0)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="ad-ov-footer">
                <span className="ad-ov-badge">
                  {user.user_type === 'admin' ? 'Admin' : 'Property Manager'}
                </span>
                <span className="ad-ov-user">{user.username}</span>
              </div>
            </section>

          </div>

          </>}

        </div>
      </main>

      {chatSession && (
        <ChatModal sessionId={chatSession} onClose={() => setChatSession(null)} />
      )}
    </div>
  );
}
